import React from 'react';
import { motion } from 'framer-motion';
import { Check, Lock, Star, BookOpen, Layers } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { useLanguage } from '../hooks/useLanguage';
export function CTASection() {
  const {
    t
  } = useLanguage();
  return <section id="pricing" className="py-24 px-4 relative" data-id="element-13">
      <div className="absolute inset-0 bg-gradient-to-t from-[#D4A843]/5 to-transparent pointer-events-none" data-id="element-14" />

      <div className="max-w-5xl mx-auto text-center" data-id="element-15">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-16">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-17">
            {t('cta_h2')}
          </h2>
          <p className="text-xl text-gray-300 mb-4" data-id="element-18">{t('cta_subtitle')}</p>
          <p className="text-sm text-[#D4A843] mb-12" data-id="element-19">{t('cta_bilingual')}</p>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto" data-id="element-20">
            {/* Tier 1: eBook Only */}
            <Card className="relative overflow-visible text-left" data-id="element-21">
              <div className="absolute -top-3 left-6 bg-[#D4A843] text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg" data-id="element-22">
                {t('cta_tier1_badge')}
              </div>

              <div className="pt-6" data-id="element-23">
                <div className="flex items-center gap-3 mb-4" data-id="element-24">
                  <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center" data-id="element-25">
                    <BookOpen className="w-5 h-5 text-[#D4A843]" data-id="element-26" />
                  </div>
                  <h3 className="text-xl font-bold text-white" data-id="element-27">
                    {t('cta_tier1_title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-28">
                  <div className="text-gray-500 text-sm mb-1" data-id="element-29">
                    {t('cta_tier1_price_label')}{' '}
                    <span className="line-through" data-id="element-30">$19.99</span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-31">
                    <span className="text-5xl font-extrabold text-white" data-id="element-32">
                      $6.36
                    </span>
                    <span className="text-gray-400 text-sm" data-id="element-33">
                      {t('cta_tier1_price_unit')}
                    </span>
                  </div>
                  <div className="text-[#22C55E] text-sm font-semibold mt-1" data-id="element-34">
                    {t('cta_tier1_savings')}
                  </div>
                </div>

                <ul className="space-y-3 mb-8" data-id="element-35">
                  {[t('cta_tier1_f1'), t('cta_tier1_f2'), t('cta_tier1_f3'), t('cta_tier1_f4'), t('cta_tier1_f5')].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-36">
                      <Check className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-37" />
                      <span data-id="element-38">{item}</span>
                    </li>)}
                </ul>

                <Button size="lg" variant="outline" fullWidth className="mb-3" href="https://vitalrutinas.com/cart/53266064376176:1" data-id="element-39">
                  {t('cta_tier1_cta')}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-40">
                  <Lock className="w-3 h-3" data-id="element-41" />
                  {t('cta_tier1_secure')}
                </div>
              </div>
            </Card>

            {/* Tier 2: Bundle eBook + App */}
            <Card variant="highlight" className="relative overflow-visible text-left ring-2 ring-[#D4A843]/40" data-id="element-42">
              <div className="absolute -top-3 left-6 bg-[#DC2626] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg" data-id="element-43">
                {t('cta_tier2_badge')}
              </div>

              <div className="pt-6" data-id="element-44">
                <div className="flex items-center gap-3 mb-4" data-id="element-45">
                  <div className="w-10 h-10 rounded-lg bg-[#D4A843]/20 flex items-center justify-center" data-id="element-46">
                    <Layers className="w-5 h-5 text-[#D4A843]" data-id="element-47" />
                  </div>
                  <h3 className="text-xl font-bold text-white" data-id="element-48">
                    {t('cta_tier2_title')}
                  </h3>
                </div>

                <p className="text-xs text-gray-400 mb-3" data-id="element-49">
                  {t('cta_tier2_subtitle')}
                </p>

                <div className="mb-6" data-id="element-50">
                  <div className="text-gray-500 text-sm mb-1" data-id="element-51">
                    {t('cta_tier2_price_label')}{' '}
                    <span className="line-through" data-id="element-52">$49.99</span>/año
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-53">
                    <span className="text-5xl font-extrabold text-white" data-id="element-54">
                      $24.99
                    </span>
                    <span className="text-gray-400 text-sm" data-id="element-55">
                      {t('cta_tier2_price_unit')}
                    </span>
                  </div>
                  <div className="text-[#22C55E] text-sm font-semibold mt-1" data-id="element-56">
                    {t('cta_tier2_savings')}
                  </div>
                </div>

                <ul className="space-y-3 mb-8" data-id="element-57">
                  {[t('cta_tier2_f1'), t('cta_tier2_f2'), t('cta_tier2_f3'), t('cta_tier2_f4'), t('cta_tier2_f5'), t('cta_tier2_f6')].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-58">
                      <Check className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-59" />
                      <span data-id="element-60">{item}</span>
                    </li>)}
                </ul>

                <Button size="lg" fullWidth className="mb-3 text-lg shadow-[0_0_30px_rgba(212,168,67,0.3)]" href="https://vitalrutinas.com/cart/53266064376176:1" data-id="element-61">
                  {t('cta_tier2_cta')}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-62">
                  <Lock className="w-3 h-3" data-id="element-63" />
                  {t('cta_tier2_secure')}
                </div>
              </div>
            </Card>
          </div>

          <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-6 text-gray-400 text-sm" data-id="element-64">
            <div className="flex items-center gap-2" data-id="element-65">
              <div className="flex" data-id="element-66">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-[#D4A843] fill-[#D4A843]" data-id="element-67" />)}
              </div>
              <span data-id="element-68">{t('cta_rating')}</span>
            </div>
            <span className="hidden md:inline" data-id="element-69">•</span>
            <span data-id="element-70">{t('cta_social')}</span>
          </div>
        </motion.div>
      </div>
    </section>;
}